import ApiResourceManager from '../ApiResourceManager';

export const TAX_SCOPE = {
  COMPANY: 'company',
  PROVIDER: 'provider',
  MASTER: 'master',
};

/**
 * The axios instance built by useApiClient always stamps x-on-behalf-of-company
 * once a company is selected. Blanking it here is what makes the same
 * /tax-categories endpoints target the partner's own default set instead of
 * the selected restaurant — see ActOnBehalfOfCompany on the backend, which
 * treats an empty header the same as a missing one.
 */
const scopeHeaders = (scope) => (scope === TAX_SCOPE.PROVIDER ? { 'x-on-behalf-of-company': '' } : undefined);

/**
 * A master-menu category is owned by the franchise's service provider, not by
 * any one restaurant. TaxCategoryRequest::owner() resolves that branch ahead of
 * the company header, reading the owner off onBehalfOfFranchise()->serviceProvider
 * — so x-on-behalf-of-franchise, which useApiClient already stamps, is required
 * here rather than incidental, and is_master is what keeps the create off the
 * selected outlet. The branch takes WriteMasterMenu, deliberately not the
 * Admin-only partner-scope rule: writing a master menu is a menu right.
 *
 * The backend honours the flag only while no tax category is bound to the route.
 * tax_categories has no master column — a master-menu row and a partner default
 * are the same ServiceProvider-owned record — so honouring it on
 * show/update/delete would be a second way past that partner-scope rule.
 *
 * Here, only create sends it, and that is the only method TAX_SCOPE.MASTER ever
 * reaches: views/menu/menu/index.jsx hands the master scope to the picker's
 * inline create alone, and the master menu lists through
 * MenusManager.taxCategories() (GET /menu/tax-categories), not this manager.
 * index() below does not carry the flag, so calling it with TAX_SCOPE.MASTER
 * would quietly return the selected outlet's categories — don't.
 *
 * Backend side of this lands with shop-backend !1323 (ae8a5d12a). Until it
 * merges, owner() is onBehalfOfCompany() ?? onBehalfOfProvider(), the flag is
 * ignored, and a master create lands on whichever outlet is selected.
 */
const scopeBody = (scope) => (scope === TAX_SCOPE.MASTER ? { is_master: true } : undefined);

export default class TaxCategoriesManager extends ApiResourceManager {
  index = ({ scope }) =>
    this.request({
      method: 'GET',
      url: '/tax-categories',
      headers: scopeHeaders(scope),
    });

  create = ({ scope, ...data }) =>
    this.request({
      method: 'POST',
      url: '/tax-categories',
      data: { ...data, ...scopeBody(scope) },
      headers: scopeHeaders(scope),
    });

  /**
   * On a category this restaurant does not own (the region's or the partner's)
   * the backend forks it into the restaurant under the same name rather than
   * rewriting it, and then requires `acknowledged_affected`.
   */
  update = ({ scope, id, ...data }) =>
    this.request({
      method: 'PUT',
      url: `/tax-categories/${id}`,
      data,
      headers: scopeHeaders(scope),
    });

  /**
   * How many products and categories a save would move onto the edited rates,
   * echoed back on the save as `acknowledged_affected`.
   */
  impact = ({ scope, id }) =>
    this.request({
      method: 'GET',
      url: `/tax-categories/${id}/impact`,
      headers: scopeHeaders(scope),
    });

  /**
   * Undoes a fork: rows linked to this owned copy go back to the same-named
   * category it shadows, and the copy is deleted (is_resettable on the list).
   */
  reset = ({ scope, id, acknowledgedAffected }) =>
    this.request({
      method: 'POST',
      url: `/tax-categories/${id}/reset`,
      data: { acknowledged_affected: acknowledgedAffected },
      headers: scopeHeaders(scope),
    });

  remove = ({ scope, id }) =>
    this.request({
      method: 'DELETE',
      url: `/tax-categories/${id}`,
      headers: scopeHeaders(scope),
    });
}
